// Follow Up History Component
// Displays follow-up messages timeline

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Send, Ban, RefreshCw } from 'lucide-react';

const stepLabels = {
  1: 'Immediate',
  2: '24 Hours',
  3: '3 Days',
  4: '7 Days'
};

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  sent: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-500'
};

const FollowUpHistory = ({ followUps, onCancel, onResend }) => {
  if (!followUps || followUps.length === 0) return null;

  return (
    <Card className="border-slate-200" data-testid="followup-history-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Send className="w-5 h-5 text-purple-500" />
          Follow-up Messages ({followUps.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {followUps.map((fu, index) => (
            <div 
              key={fu.id} 
              className="p-3 bg-slate-50 rounded-lg border border-slate-200"
              data-testid={`followup-${index}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900 text-sm">
                    {stepLabels[fu.sequence_step] || `Step ${fu.sequence_step}`}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${fu.message_type === 'sms' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                    {fu.message_type === 'sms' ? 'SMS' : 'Email'}
                  </span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[fu.status]}`}>
                  {fu.status.charAt(0).toUpperCase() + fu.status.slice(1)}
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                {fu.status === 'sent' && fu.sent_at 
                  ? `Sent: ${new Date(fu.sent_at).toLocaleString()}`
                  : `Scheduled: ${new Date(fu.scheduled_at).toLocaleString()}`
                }
              </p>
              <p className="text-sm text-slate-600 line-clamp-2">{fu.subject}</p>
              
              {fu.status === 'pending' && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="mt-2 text-red-600 hover:text-red-700 h-8 text-xs"
                  onClick={() => onCancel(fu.id)}
                >
                  <Ban className="w-3 h-3 mr-1" />
                  Cancel
                </Button>
              )}
              
              {fu.status === 'failed' && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="mt-2 text-teal-600 hover:text-teal-700 h-8 text-xs"
                  onClick={() => onResend(fu.id)}
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Resend
                </Button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default FollowUpHistory;
